import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import SEO from '@/components/site/SEO';
import { Plus, Minus, ArrowRight, Percent, IndianRupee, Users, ShieldCheck, BadgeCheck, Handshake } from 'lucide-react';

const faqs = [
  {
    q: 'Why is applying through Finirio cheaper than going to the bank directly?',
    a: 'Two reasons. First, banks give Finirio customers a lower negotiated interest rate because Finirio delivers bulk loan business every month. Second, on charges: a ₹1 Crore file that costs around ₹60,000 directly costs only ₹30,000–₹45,000 through Finirio, because Finirio pays the difference to the bank on the customer\u2019s behalf.',
  },
  {
    q: 'How does Finirio make money if the service is free for customers?',
    a: 'Banks pay Finirio a marketing/business fee for the consistent monthly loan volume it brings them. The customer is never charged — the model is funded entirely by the banks.',
  },
  {
    q: 'Is the lower interest rate through Finirio genuine?',
    a: 'Yes. It works exactly like group bargaining: when 5–6 friends approach a branch manager together, they can negotiate a lower rate. Finirio does this at scale — banks compete for its monthly volume and extend better rates to its customers, and the bank\u2019s own banker remains involved in every transaction for transparency.',
  },
  {
    q: 'Do I lose direct contact with the bank if I apply through Finirio?',
    a: 'No. The banker stays involved in the transaction throughout, and you can speak to the bank directly at any time. In addition, Finirio assigns a free dedicated relationship manager who does the follow-ups and paperwork for you.',
  },
  {
    q: 'Which loans can I get through Finirio?',
    a: 'Home loans (from 7%*), loan against property up to ₹10 Crore, collateral-free business loans up to ₹50 Lakh, personal loans up to ₹40 Lakh, and OD/CC working capital up to ₹5 Crore — compared across 20+ partner banks.',
  },
  {
    q: 'Does Finirio serve customers outside Mumbai?',
    a: 'Yes. Finirio is headquartered in Mumbai and serves customers across India with digital applications and doorstep document collection.',
  },
];

const comparison = [
  ['Rate of interest', 'Standard card rate for walk-in customers', 'Lower negotiated rate via bulk monthly business'],
  ['Charges on a ₹1 Crore file', 'Around ₹60,000', '₹30,000–₹45,000 (Finirio pays the difference)'],
  ['Service fee to customer', 'None, but no support either', 'Zero — banks pay Finirio'],
  ['Support', 'You follow up with the branch yourself', 'Free dedicated relationship manager + direct banker access'],
  ['Bank options', 'One bank at a time', 'Offers compared across 20+ banks & NBFCs'],
  ['Documentation', 'Visit the branch', 'Doorstep document collection'],
  ['Typical disbursal', 'Varies by branch', 'Sanction in 3–5 days, disbursal in ~7 days'],
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const USPCard = ({ icon: Icon, title, children }) => (
  <div className="card-lux p-7 h-full">
    <div className="w-12 h-12 rounded-xl grid place-items-center mb-4 border border-[#1e73be]/12" style={{ background: 'linear-gradient(160deg,#eaf3fb,#f5faff)' }}>
      <Icon className="w-5 h-5" style={{ color: '#1e73be' }} />
    </div>
    <h3 className="font-heading text-[19px] font-bold text-[#0b2545] mb-2">{title}</h3>
    <p className="text-[15px] text-slate-600 leading-relaxed">{children}</p>
  </div>
);

const WhyFinirio = () => {
  const [open, setOpen] = useState(0);
  return (
    <main>
      <SEO
        title="Why Apply Through Finirio? Lower Interest Rate + 25-50% Lower Charges, Zero Fee | Finirio"
        description="Finirio's bulk business with 20+ banks gets customers lower home loan interest rates and lower charges: ₹30,000–₹45,000 instead of ~₹60,000 on a ₹1 Crore file. Free relationship manager, zero customer fee. Mumbai & India."
        path="/why-finirio"
        keywords="why use loan agent, home loan lower interest rate, home loan charges saving, loan distributor mumbai, finirio vs bank direct, cheapest home loan mumbai"
        jsonLd={jsonLd}
        jsonLdId="why-finirio-jsonld"
      />
      <PageBanner title="Why Finirio" crumbs={['Why Finirio']} />

      {/* Direct answer block */}
      <section className="bg-white py-14">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6 text-center">
          <h1 className="font-heading text-[28px] md:text-[36px] font-bold text-slate-900 tracking-tight leading-tight">
            Why apply for a loan through <span style={{ color: '#1e73be' }}>Finirio</span> instead of going to the bank directly?
          </h1>
          <p className="mt-5 text-slate-700 text-[16px] md:text-[17px] leading-relaxed">
            You get a <b>lower interest rate</b> and <b>25–50% lower charges</b> than walking into a bank yourself — at
            <b> zero cost</b>. Banks pay Finirio for the bulk business it brings, so customers are never charged. On a
            ₹1 Crore file, that means paying about <b>₹30,000–₹45,000</b> in charges instead of ~₹60,000, plus a free
            dedicated relationship manager.
          </p>
        </div>
      </section>

      {/* USP cards */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <USPCard icon={Handshake} title="Bulk negotiation = lower rate of interest">
              Just as 5–6 friends approaching a Branch Manager together can negotiate a lower rate, Finirio brings
              banks a large volume of loan files every month. In return, partner banks offer Finirio customers a lower
              rate of interest than an individual walk-in customer gets for the same profile.
            </USPCard>
            <USPCard icon={IndianRupee} title="₹15,000–₹30,000 saved on charges">
              On a ₹1 Crore home loan file, direct-to-bank charges are typically around ₹60,000. Through Finirio you
              pay only ₹30,000–₹45,000 — Finirio pays the remaining difference to the bank on your behalf.
            </USPCard>
            <USPCard icon={Percent} title="Zero fee — banks pay, not you">
              Finirio never charges customers for advisory or processing. Banks pay Finirio a marketing/business fee
              for the monthly loan volume it brings, so the service stays completely free for you.
            </USPCard>
            <USPCard icon={Users} title="Free relationship manager + banker in the loop">
              Every customer gets a dedicated relationship manager, free of cost, handling everything from doorstep
              document pickup to disbursal. The bank&apos;s own banker also stays involved, so you can talk to the bank
              directly at any time for full transparency.
            </USPCard>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-white py-14">
        <div className="max-w-[1000px] mx-auto px-4 lg:px-6">
          <h2 className="font-heading text-[26px] md:text-[32px] font-bold text-slate-900 text-center mb-8">
            Direct to bank vs through Finirio
          </h2>
          <div className="overflow-x-auto rounded-xl border border-[#0b2545]/10 shadow-[0_18px_40px_-24px_rgba(11,37,69,.3)]">
            <table className="w-full text-left text-[14px] md:text-[15px]">
              <thead>
                <tr className="text-white navy-panel">
                  <th className="px-4 py-3 font-semibold">Factor</th>
                  <th className="px-4 py-3 font-semibold">Directly at the bank</th>
                  <th className="px-4 py-3 font-semibold">Through Finirio</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map(([f, d, v], i) => (
                  <tr key={i} className={i % 2 ? 'bg-[#fbf9f4]' : 'bg-white'}>
                    <td className="px-4 py-3 font-semibold text-slate-800">{f}</td>
                    <td className="px-4 py-3 text-slate-600">{d}</td>
                    <td className="px-4 py-3 text-slate-800">
                      <span className="inline-flex items-start gap-1.5">
                        <BadgeCheck className="w-4 h-4 mt-0.5 flex-none" style={{ color: '#2ea690' }} />
                        {v}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/home-loan" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-white text-[15px] font-semibold hover:brightness-110 transition" style={{ backgroundColor: '#1e73be' }}>
              Apply for Home Loan <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/home-loan-guide-mumbai" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-[15px] font-semibold border border-[#1e73be] text-[#1e73be] hover:bg-[#1e73be]/5 transition">
              Read the Home Loan Guide
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6">
          <h2 className="font-heading text-[26px] md:text-[32px] font-bold text-slate-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className={`border rounded-lg bg-white transition-colors ${isOpen ? 'border-[#1e73be]/40' : 'border-slate-200'}`}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full flex items-center justify-between text-left px-5 py-4" aria-expanded={isOpen}>
                    <span className="font-heading font-semibold text-slate-900 text-[15px] md:text-[16px] pr-4">{f.q}</span>
                    <span className="w-8 h-8 rounded-full grid place-items-center flex-none" style={{ backgroundColor: isOpen ? '#1e73be' : '#eaf3fb', color: isOpen ? 'white' : '#1e73be' }}>
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-slate-700 text-[15px] leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-white py-12">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3" style={{ color: '#2ea690' }} />
          <p className="text-slate-700 text-[16px] leading-relaxed">
            10,000+ customers · 20+ partner banks · ₹5000 Cr+ disbursed · 4.8/5 rating —
            with the banker involved in every transaction for complete transparency.
          </p>
        </div>
      </section>
    </main>
  );
};

export default WhyFinirio;
